import { useMemo } from 'react';
import { Link, useLocation, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ChevronRight } from '@/components/icons';

/** Map each path segment (or path prefix) to a translation key. */
const SEGMENT_LABELS: Record<string, string> = {
  '': 'nav.dashboard',
  articles: 'nav.articles',
  categories: 'articles.categories.title',
  families: 'articles.families',
  brands: 'articles.brands',
  clients: 'nav.clients',
  suppliers: 'nav.suppliers',
  sales: 'nav.sales',
  quotes: 'nav.quotes',
  orders: 'nav.orders',
  deliveries: 'nav.deliveries',
  invoices: 'nav.invoices',
  'credit-notes': 'nav.creditNotes',
  purchases: 'nav.purchases',
  receptions: 'nav.receptions',
  stock: 'nav.stockModule',
  warehouses: 'nav.warehouses',
  inventories: 'nav.inventories',
  payments: 'nav.paymentsNav',
  treasury: 'nav.treasuryNav',
  checks: 'nav.checksOnly',
  traites: 'nav.traites',
  settings: 'nav.settings',
  company: 'nav.settingsCompany',
  branding: 'nav.settingsBranding',
  'table-columns': 'nav.settingsColumns',
  new: 'common.create',
};

/** Context-aware labels: when a segment appears under a specific parent. */
const CONTEXT_LABELS: Record<string, Record<string, string>> = {
  purchases: {
    orders: 'nav.purchaseOrders',
    invoices: 'nav.purchaseInvoices',
    'credit-notes': 'nav.purchaseCreditNotes',
  },
};

export function Breadcrumb() {
  const { t } = useTranslation();
  const location = useLocation();
  const params = useParams();

  const crumbs = useMemo(() => {
    const segments = location.pathname.split('/').filter(Boolean);
    const items: { label: string; href: string }[] = [
      { label: t('nav.dashboard'), href: '/' },
    ];

    let path = '';
    for (let i = 0; i < segments.length; i++) {
      const seg = segments[i]!;
      path += `/${seg}`;

      // Skip dynamic :id segments - they're handled as detail pages below.
      if (params.id && seg === params.id) {
        const parent = segments[i - 1];
        items.push({
          label: parent === 'articles' ? t('articles.profile.tabs.details') : t('common.edit'),
          href: path,
        });
        continue;
      }

      // Context-aware label (e.g., "orders" under "purchases" → "Purchase orders")
      const parent = segments[i - 1];
      const contextLabel = parent ? CONTEXT_LABELS[parent]?.[seg] : undefined;
      const labelKey = contextLabel ?? SEGMENT_LABELS[seg];

      if (labelKey) {
        items.push({ label: t(labelKey), href: path });
      }
    }

    return items;
  }, [location.pathname, params.id, t]);

  if (crumbs.length <= 1) return null;

  return (
    <nav className="flex flex-wrap items-center gap-y-1 gap-x-1 text-sm text-muted-foreground mb-4">
      {crumbs.map((crumb, i) => {
        const isLast = i === crumbs.length - 1;
        return (
          <span key={crumb.href} className="flex items-center gap-1">
            {i > 0 && <ChevronRight size={12} className="text-muted-foreground/50 rtl:rotate-180" />}
            {isLast ? (
              <span className="font-medium text-foreground">{crumb.label}</span>
            ) : (
              <Link to={crumb.href} className="hover:text-foreground transition-colors">
                {crumb.label}
              </Link>
            )}
          </span>
        );
      })}
    </nav>
  );
}
